/**
 * Collection credit review request types — bounded review artifact from credit-hold escalation.
 *
 * Not CreditHold enforcement · not CreditHoldRequest · not universal ReviewRequest.
 */

export const COLLECTION_CREDIT_REVIEW_REQUEST_CODES = [
  'credit_review_from_hold_escalation_v0',
] as const;

export type CollectionCreditReviewRequestCode =
  (typeof COLLECTION_CREDIT_REVIEW_REQUEST_CODES)[number];

export const COLLECTION_CREDIT_REVIEW_REQUEST_SUBJECT_TYPES = ['collection_case'] as const;

export type CollectionCreditReviewRequestSubjectType =
  (typeof COLLECTION_CREDIT_REVIEW_REQUEST_SUBJECT_TYPES)[number];

export const COLLECTION_CREDIT_REVIEW_REQUEST_STATUSES = [
  'open',
  'assigned',
  'deferred',
  'resolved',
  'cancelled',
] as const;

export type CollectionCreditReviewRequestStatus =
  (typeof COLLECTION_CREDIT_REVIEW_REQUEST_STATUSES)[number];

export const COLLECTION_CREDIT_REVIEW_REQUEST_TERMINAL_STATUSES: readonly CollectionCreditReviewRequestStatus[] =
  ['resolved', 'cancelled'] as const;

export const COLLECTION_CREDIT_REVIEW_REQUEST_ACTIVE_STATUSES: readonly CollectionCreditReviewRequestStatus[] =
  ['open', 'assigned', 'deferred'] as const;

export const COLLECTION_CREDIT_REVIEW_REQUEST_ASSIGNED_TO_TYPES = ['user'] as const;

export type CollectionCreditReviewRequestAssignedToType =
  (typeof COLLECTION_CREDIT_REVIEW_REQUEST_ASSIGNED_TO_TYPES)[number];

export const COLLECTION_CREDIT_REVIEW_REQUEST_RESOLUTION_CODES = [
  'no_action_required',
  'continue_monitoring',
  'request_more_information',
  'refer_for_hold_decision',
] as const;

export type CollectionCreditReviewRequestResolutionCode =
  (typeof COLLECTION_CREDIT_REVIEW_REQUEST_RESOLUTION_CODES)[number];

export const COLLECTION_CREDIT_REVIEW_REQUEST_SOURCE_FINGERPRINT_MAX_LENGTH = 128;

export const COLLECTION_CREDIT_REVIEW_REQUEST_SUMMARY_MAX_LENGTH = 500;

export const COLLECTION_CREDIT_REVIEW_REQUEST_RESOLUTION_NOTE_MAX_LENGTH = 2000;

export const COLLECTION_CREDIT_REVIEW_REQUEST_ENFORCEMENT_BOUNDARY_COPY =
  '신용 검토 요청입니다. 신용 보류가 자동 적용되지 않습니다.' as const;

export interface CollectionCreditReviewRequest {
  id: string;
  tenantId: string;
  requestCode: CollectionCreditReviewRequestCode;
  subjectType: CollectionCreditReviewRequestSubjectType;
  subjectId: string;
  collectionCaseId: string;
  arEntryId: string;
  externalEntityId: string;
  sourceRecommendationId: string;
  sourceDispositionId: string;
  sourceFingerprint: string;
  sourceSnapshotSchemaVersion: string;
  sourceSnapshotHash: string;
  status: CollectionCreditReviewRequestStatus;
  reasonCodes: string[];
  rationaleCode: string;
  summary: string;
  assignedToType: CollectionCreditReviewRequestAssignedToType | null;
  assignedToId: string | null;
  deferredUntil: string | null;
  resolutionCode: CollectionCreditReviewRequestResolutionCode | null;
  resolutionNote: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  version: number;
}

export interface CreateCollectionCreditReviewRequestInput {
  tenantId: string;
  requestCode: CollectionCreditReviewRequestCode;
  subjectType: CollectionCreditReviewRequestSubjectType;
  subjectId: string;
  collectionCaseId: string;
  arEntryId: string;
  externalEntityId: string;
  sourceRecommendationId: string;
  sourceDispositionId: string;
  sourceFingerprint: string;
  sourceSnapshotSchemaVersion: string;
  sourceSnapshotHash: string;
  reasonCodes: string[];
  rationaleCode: string;
  summary: string;
  createdBy: string;
}

export interface CollectionCreditReviewRequestOccurrenceIdentity {
  tenantId: string;
  sourceRecommendationId: string;
  sourceFingerprint: string;
}

export function isCollectionCreditReviewRequestCode(
  value: string
): value is CollectionCreditReviewRequestCode {
  return (COLLECTION_CREDIT_REVIEW_REQUEST_CODES as readonly string[]).includes(value);
}

export function isCollectionCreditReviewRequestStatus(
  value: string
): value is CollectionCreditReviewRequestStatus {
  return (COLLECTION_CREDIT_REVIEW_REQUEST_STATUSES as readonly string[]).includes(value);
}

export function isCollectionCreditReviewRequestResolutionCode(
  value: string
): value is CollectionCreditReviewRequestResolutionCode {
  return (COLLECTION_CREDIT_REVIEW_REQUEST_RESOLUTION_CODES as readonly string[]).includes(value);
}

export function isCollectionCreditReviewRequestActiveStatus(
  status: CollectionCreditReviewRequestStatus
): boolean {
  return (COLLECTION_CREDIT_REVIEW_REQUEST_ACTIVE_STATUSES as readonly string[]).includes(status);
}

export function isCollectionCreditReviewRequestTerminalStatus(
  status: CollectionCreditReviewRequestStatus
): boolean {
  return (COLLECTION_CREDIT_REVIEW_REQUEST_TERMINAL_STATUSES as readonly string[]).includes(status);
}
